import { supabase } from "@/integrations/supabase/client";

interface Promotion {
  id: string;
  name: string;
  discount_percentage: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface PromotionWithCourses extends Promotion {
  promotion_courses: { course_id: string }[];
}

interface CachedPromotions {
  data: PromotionWithCourses[];
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let cachedPromotions: CachedPromotions | null = null;

export async function fetchActivePromotions(): Promise<PromotionWithCourses[]> {
  // Check cache
  if (cachedPromotions && Date.now() - cachedPromotions.timestamp < CACHE_DURATION) {
    return cachedPromotions.data;
  }

  const now = new Date().toISOString();
  
  // Single batch query for all active promotions
  const { data, error } = await supabase
    .from('promotions')
    .select(`
      id,
      name,
      discount_percentage,
      start_date,
      end_date,
      is_active,
      promotion_courses(course_id)
    `)
    .eq('is_active', true)
    .lte('start_date', now)
    .gte('end_date', now);

  if (error) {
    console.error("Error fetching promotions:", error);
    return [];
  }

  const promotions = (data || []) as PromotionWithCourses[];
  
  // Cache the result
  cachedPromotions = {
    data: promotions,
    timestamp: Date.now()
  };

  return promotions;
}

export function getPromotionForCourse(
  courseId: string,
  allPromotions: PromotionWithCourses[]
): Promotion | null {
  // Find site-wide promotion or course-specific promotion
  const promotion = allPromotions.find(promo => {
    // Site-wide if no specific courses
    if (!promo.promotion_courses || promo.promotion_courses.length === 0) {
      return true;
    }
    // Course-specific
    return promo.promotion_courses.some(pc => pc.course_id === courseId);
  });

  return promotion || null;
}

export function clearPromotionCache(): void {
  cachedPromotions = null;
}
